import { siteRequest, AccessError } from "./siteCustomerApi";
export async function openPdf(resourceId, signal, onFailure) {
  const pdf = await import("pdfjs-dist/build/pdf.mjs");
  if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
  const port = new Worker(
    new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url),
    { type: "module" },
  );
  const worker = new pdf.PDFWorker({ port });
  let task,
    timer,
    stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearInterval(timer);
    task?.destroy().catch(() => {});
    worker.destroy();
    port.terminate();
  };
  signal.addEventListener("abort", stop, { once: true });
  try {
    const issued = await siteRequest("/files/tickets", {
      method: "POST",
      body: { resourceId, purpose: "preview" },
      signal,
    });
    const launched = await siteRequest("/files/preview", {
      method: "POST",
      body: { ticket: issued.data.ticket },
      signal,
    });
    const headers = { "X-Site-Preview": launched.data.previewSession };
    const head = await siteRequest("/files/preview", {
      method: "HEAD",
      headers,
      raw: true,
      signal,
    });
    const size = Number(head.headers.get("content-length"));
    if (
      !Number.isInteger(size) ||
      size < 1 ||
      size > 50 * 1024 * 1024 ||
      head.headers.get("content-type") !== "application/pdf"
    )
      throw new AccessError("file_unavailable");
    const range = async (begin, end) => {
      const response = await siteRequest("/files/preview", {
        headers: { ...headers, Range: `bytes=${begin}-${end - 1}` },
        raw: true,
        signal,
      });
      if (
        response.status !== 206 ||
        response.headers.get("content-range") !==
          `bytes ${begin}-${end - 1}/${size}`
      )
        throw new AccessError("connection_unavailable");
      const data = new Uint8Array(await response.arrayBuffer());
      if (data.byteLength !== end - begin)
        throw new AccessError("network_error");
      return data;
    };
    const initial = await range(0, Math.min(262144, size));
    const transport = new pdf.PDFDataRangeTransport(size, initial);
    transport.requestDataRange = (begin, end) => {
      range(begin, end)
        .then((data) => {
          if (!stopped) transport.onDataRange(begin, data);
        })
        .catch((error) => {
          if (!stopped) {
            stop();
            onFailure(error);
          }
        });
    };
    transport.abort = () => {};
    task = pdf.getDocument({
      range: transport,
      worker,
      disableStream: true,
      disableAutoFetch: true,
      rangeChunkSize: 262144,
      isEvalSupported: false,
      useWasm: false,
      useWorkerFetch: false,
      cMapUrl: `/static/site-pdf/${pdf.version}/cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `/static/site-pdf/${pdf.version}/standard_fonts/`,
    });
    let loadingTimeout;
    const document = await Promise.race([
      task.promise,
      new Promise((_, reject) => {
        loadingTimeout = setTimeout(
          () => reject(new AccessError("network_error")),
          60000,
        );
      }),
    ]).finally(() => clearTimeout(loadingTimeout));
    const expiresAt = Date.now() + launched.data.expiresIn * 1000;
    timer = setInterval(async () => {
      if (stopped) return;
      try {
        if (Date.now() >= expiresAt) throw new AccessError("preview_expired");
        await siteRequest("/files/preview", {
          method: "HEAD",
          headers,
          raw: true,
          signal,
        });
      } catch (error) {
        if (!stopped) {
          stop();
          onFailure(error);
        }
      }
    }, 30000);
    return { document, close: stop, expiresAt };
  } catch (error) {
    stop();
    throw error;
  }
}
